import tokens from '../tokens.json';

export type ThemeMode = 'light' | 'dark';
export type ThemePalette = Partial<typeof tokens.semantic.light>;
export interface ThemeConfig {
  fontFamily?: string;
  fontScale?: number;
  radius?: number;
  touchTarget?: number;
  light?: ThemePalette;
  dark?: ThemePalette;
}
export type ThemeVariables = Record<`--${string}`, string | number>;
const kebab = (value: string) => value.replace(/[A-Z]/g, letter => '-' + letter.toLowerCase());
function contrastInk(color: string): string | undefined {
  const hex = color.match(/^#([0-9a-f]{6}|[0-9a-f]{3})$/i)?.[1];
  if (!hex) return undefined;
  const full = hex.length === 3 ? [...hex].map(c => c + c).join('') : hex;
  const rgb = [0, 2, 4].map(i => parseInt(full.slice(i, i + 2), 16) / 255).map(v => v <= .04045 ? v / 12.92 : ((v + .055) / 1.055) ** 2.4);
  const light = rgb[0] * .2126 + rgb[1] * .7152 + rgb[2] * .0722;
  return (light + .05) / .05 >= 1.05 / (light + .05) ? '#000000' : '#ffffff';
}
/** CSS custom properties for an app root (html for portals), never mutates the baseline. */
export function createThemeVariables(config: ThemeConfig = {}, mode: ThemeMode = 'light'): ThemeVariables {
  const overrides = config[mode] ?? {};
  const palette = {...tokens.semantic[mode], ...overrides};
  if (overrides.accent) {
    palette.focus = overrides.focus ?? overrides.accent;
    palette.accentHover = overrides.accentHover ?? `color-mix(in srgb, ${overrides.accent} 88%, ${palette.ink})`;
    palette.accentSoft = overrides.accentSoft ?? `color-mix(in srgb, ${overrides.accent} 10%, ${palette.panel})`;
    palette.onAccent = overrides.onAccent ?? contrastInk(overrides.accent) ?? palette.onAccent;
  }
  for (const [key, value] of Object.entries({radius:config.radius,fontScale:config.fontScale,touchTarget:config.touchTarget})) {
    if (value !== undefined && (!Number.isFinite(value) || value < 0 || (key !== 'radius' && value === 0))) throw new RangeError(`Invalid theme ${key}`);
  }
  return {
    ...Object.fromEntries(Object.entries(palette).map(([key, value]) => ['--' + kebab(key), value])),
    '--font-family': config.fontFamily ?? tokens.base.fontFamily,
    '--font-scale': config.fontScale ?? tokens.base.fontScale,
    '--radius': `${config.radius ?? tokens.base.radius}px`,
    '--touch-target': `${config.touchTarget ?? tokens.base.touchTarget}px`,
  };
}
export const defaultTheme = tokens;
